/// <reference path="./types/chartjs.d.ts" />

import { IProject } from './projects.interface';
import { IFillLangSkillsOpt } from './options.interface';
import { getLanguageColor, toMonthString, pSBC, truncateHtml } from './utils';
import { setChartTheme } from './themes';
import { ChartEvent } from 'ChartV4Plugins';
import { LegendItem, TooltipItem } from 'ChartV4';
import { displayError } from './education'
import { createTooltips, initReadMore, refreshAOS } from './global';
import { generateProjectId } from './projects';
import { ISkillData, skillRatings } from './skills.interface';
import { Popover, Modal } from 'bootstrap';

interface ITopLang {
	name: string,
	loc: number,
	/**
	 * Language color represented in hexadecimal or rgba string.
	 */
	color: string,
	lastUsed: Date | string
}

/**
 * Receives an array with projects' data and fills the skills' language section.
 *
 * @param projects An array with projects
 * @param options
 */
export function fillLanguageSkills(projects: IProject[], options: IFillLangSkillsOpt | undefined): void {
	
	let numLang = 5;
	
	if (options?.numLang) {
		numLang = options.numLang;
	}

	let data = getTopLangsData(projects, numLang);

	fillPieChart(data);
	fillLanguageCards(data);

	if (options?.toHide) {
		data.forEach(el => {
			if (options.toHide.find(el2 => el2.toLowerCase() === el.name.toLowerCase())) {
				toggleLanguageVisibility(el.name.toLowerCase());
			}
		});
	}
}
/**
 * Adds the top language cards next to the Pie chart in the skills section.
 * 
 * @param data Top languages data to fill the cards.
 */
function fillLanguageCards(data: ITopLang[]): void {

	let container = document.querySelector('#languages-cards');
	container.innerHTML = ""; 

	let sizeClass = (data.length > 4 ? "six" : "four");

	for (const o of data) {

		let lastUsed = o.lastUsed;
		if (o.lastUsed instanceof Date) {
			lastUsed = toMonthString(o.lastUsed.getMonth()).substr(0,3) + " " + o.lastUsed.getFullYear();
		}

		/*let colorDarkened = pSBC ( -0.2, o.color );
		 border:1px solid ${colorDarkened};box-shadow: inset 0 0 0 1px ${colorDarkened};
		*/

		container.innerHTML += `
			<div class="card ${sizeClass}">
				<div class="card-header">
					<div class="circle" style="background: ${o.color} none repeat scroll 0% 0%;"></div>
					<div class="language-name">${o.name}</div>
				</div>
				<div class="card-body">LOC: <br />${o.loc.toLocaleString()}</div> 
				<div class="card-footer">Last used: <br />${lastUsed}</div>
			</div>`;
	}
}

/**
 * Get the languages with most lines of code.
 * 
 * @param projects An array with all the projects.
 * @param numLang The maximum number of languages to get.
 * @returns An array with data regarding the languages with most LOC to fill the skills section.
 */
function getTopLangsData(projects: IProject[], numLang: number): ITopLang[] {
	let data = [];

	for (const project of projects) {

		if (!project.technologies) { continue; }

		for (const tech of project.technologies) {
			if (typeof tech === 'object') {
				let name = tech.name;
				let loc = tech.linesOfCode;

				if (name.toLowerCase().startsWith("c++")) { name = "C++"; }
				else if (name.toLowerCase().startsWith("javascript")) { name = "JavaScript"; }
				else if (name.toLowerCase().startsWith("html")) {
					name = "HTML";
					continue; // not a programming language
				}
				else if (name.toLowerCase().startsWith("css")) {
					name = "CSS";
					continue; // not a programming language
				}
				else if (name.toLowerCase().startsWith("python")) { name = "Python"; }
				else if (name.toLowerCase().startsWith("java")) { name = "Java"; }
				else if (name.toLowerCase().startsWith("c#")) { name = "C#"; }
				else if (name.toLowerCase().startsWith("php")) { name = "PHP"; }
				else if (name.toLowerCase().includes("sql")) { name = "SQL"; }

				let index = data.findIndex(obj => (obj.name == name));

				let lastUsed = new Date(project.end_date);

				if (index < 0) {
					data.push({
						name: name, loc: loc, color: getLanguageColor(name),
						lastUsed: (isNaN(lastUsed.valueOf()) ? project.end_date : lastUsed)
					});
				} else {
					data[index].loc += loc;

					if (isNaN(lastUsed.valueOf())) { // e.g. Present
						data[index].lastUsed = project.end_date;
					} else if (data[index].lastUsed < lastUsed) {
						data[index].lastUsed = lastUsed;
					}
				}
			}
		}
	}

	data.sort((a, b) => b.loc - a.loc);
	data = data.slice(0, numLang);

	return data;
}

// For some reason the `hoverOffset` property isn't defined in the dataset
// interface. So I added it, based on this example:
// https://chartjs-plugin-datalabels.netlify.app/guide/typescript.html#option-context
declare module 'chart.js' {
	interface ChartDataSets {
		hoverOffset?: number;
	}
}

/**
 * Fill the Pie chart with the languages' data that is given.
 * 
 * @param data The languages' data to fill the chart.
 */
function fillPieChart(data: ITopLang[]) {

	let names: string[] = [];
	let loc: number[] = [];
	let bg_colors: string[] = [];

	for (const o of data) {
		names.push(o.name);
		loc.push(o.loc);
		bg_colors.push(o.color ? o.color : "#000");
	}
	let border_colors = bg_colors.map(el => pSBC ( -0.2, el ));

	let ctx = (document.getElementById('languages-chart') as HTMLCanvasElement).getContext('2d');

	globalThis.skillLangchart = new Chart(ctx, {
		type: "pie",
		data: {
			labels: names,
			datasets: [{
				data: loc,
				backgroundColor: bg_colors,
				borderColor: border_colors,
				borderWidth: 1,
				hoverOffset: 6,
			}]
		},
		options: {
			layout: { padding: 10 },
			maintainAspectRatio: true,
			responsive: true,
			plugins: {
				/*title: {
					display: true,
					text: 'Most used languages'
				},*/
				legend: {
					display: true,
					position: 'top',
					// The default onClick event toggles the language, but
					// I want to toggle it on the cards outside the chart as well
					onClick: function (event: ChartEvent, elem: LegendItem) {
						toggleLanguageVisibility(elem.text);
					}
				},
				tooltip: {
					bodyFont: { weight: 'bold' },
					footerFont: { weight: 'normal' },
					callbacks: {
						title: function(tooltipItems: TooltipItem<"pie">[]) {
							//var info = tooltipItems[0].label
							//return info;
							return '';
						},
						label: function(tooltipItem: TooltipItem<"pie">) {
							return tooltipItem.label;
						},
						/*afterLabel: function(tooltipItem) {
							var thisLoc = tooltipItem.raw;
							var sum = loc.reduce((x, y) => x + y);
							var info = `${(thisLoc*100/sum).toFixed(2)}%`;
							info += '\nLOC: ' + tooltipItem.formattedValue;
							return info;
						},*/
						footer: function(tooltipItems: TooltipItem<"pie">[]) {
							const thisLoc = parseInt(tooltipItems[0].raw as string);
							const sum = getVisibleLocSum(loc);//loc.reduce((x, y) => x + y);
							let info = `${(thisLoc*100/sum).toFixed(2)}%`;
							info += '\nLOC: ' + tooltipItems[0].formattedValue;
							return info;
						}
					}
				}
			}
		}
	});

	globalThis.skillLangchart.render();
	setChartTheme(globalThis.skillLangchart);
}

/**
 * Gets the sum of the lines of code of the languages that are
 * visible in the chart. Excludes the ones that are hidden, for
 * example by clicking on a legend label item.
 * 
 * @param loc Array with the lines of code of the languages in the chart.
 * @returns The sum of the lines of code of the visible languages in the chart.
 */
function getVisibleLocSum(loc: number[]): number {
	let sum = 0;
	let ci = globalThis.skillLangchart;
	let legendItems = ci.legend.legendItems;
	
	for (let index = 0; index < legendItems.length; ++index) {
		let visible = ci.getDataVisibility(index);
		if (visible) {
			sum += loc[index];
		}
	}
	return sum;
}

/**
 * Changes the visibility of a language on the chart and cards.
 * 
 * @param lang The language to toggle visibility for.
 */
function toggleLanguageVisibility(lang: string) {

	// Update chart
	toggleLanguageVisibilityChart(lang);

	// Update cards
	toggleLanguageVisibilityCard(lang);

}

/**
 * Changes the visibility of a language on the chart.
 * 
 * @param lang The language to toggle visibility for.
 */
// https://www.chartjs.org/docs/latest/configuration/legend.html
function toggleLanguageVisibilityChart(lang: string) {

	let ci = globalThis.skillLangchart;
	let legendItems = ci.legend.legendItems;
	
	for (let index = 0; index < legendItems.length; ++index) {
		let legendItem = legendItems[index];
		if (legendItem.text.toLowerCase() === lang.toLowerCase()) {
			// let visible = chart.getDataVisibility(index);
			ci.toggleDataVisibility(index);
			ci.update();
			break;
		}
	}
}

/**
 * Changes the visibility of a language on the cards.
 * 
 * @param lang The language to toggle visibility for.
 */
function toggleLanguageVisibilityCard(lang: string) {

	let card = getCardWithLang(lang);

	/* How to 'display' transition: https://www.impressivewebs.com/animate-display-block-none/ */
	if (card.classList.contains('hidden')) {
		card.classList.remove('hidden');
		setTimeout(function () {
			card.classList.remove('visuallyhidden');
			resizeLangCards();
		}, 0);
	} else {
		card.classList.add('visuallyhidden');
		card.addEventListener('transitionend', function(e) {
			card.classList.add('hidden');
			resizeLangCards();
		}, { capture: false, once: true, passive: false });
	}
}

/**
 * Get the card DIV element of a given language name.
 * 
 * @param lang The language name to get the card from.
 * @returns The DIV element containing of the specified language.
 */
function getCardWithLang(lang: string): HTMLDivElement {
	return Array.from(document.querySelectorAll('#languages-cards .language-name'))
	            .find(el => el.textContent.toLowerCase().includes(lang.toLowerCase()))
	            .parentNode.parentNode as HTMLDivElement;
}

/**
 * Resizes the size of the languages' DIV cards depending on how many
 * are visible.
 */
function resizeLangCards() {

	let cards = document.querySelectorAll('#languages-cards .card:not(.hidden)');

	let sizeClass = (cards.length > 4 ? "six" : "four");

	for (let card of cards) {
		card.classList.remove('two');
		card.classList.remove('four');
		card.classList.remove('six');
		card.classList.add(sizeClass);
	}
}

// Function to fetch JSON file
async function loadSkillsJson(): Promise<ISkillData> {
	const response = await fetch('json/skills.json?v=4');
	if (response.ok) {
		return await response.json();
	} else {
		throw new Error("Error fetching skills: " + response.status);
	}
}

/**
 * Fills the skills section with data from the skills JSON file.
 * @param skillData The skills data object loaded from the JSON file.
 * @param projects The list of projects to link with skills.
 */
function fillSkillSection(skillData: ISkillData, projects: IProject[]) {
	const tabList = document.getElementById('categoryTabs');
	const tabContent = document.getElementById('categoryContent');
	if (!tabList || !tabContent) return;

	// Loop over categories to create tabs and tab panes
	skillData.categories.forEach((category, index) => {
		const isActive = index === 0 ? 'active' : '';
		const tabHTML = `
			<li class="nav-item" role="presentation">
				<button class="nav-link ${isActive}" id="tab-${index}" data-bs-toggle="tab" data-bs-target="#content-${index}" type="button" role="tab" aria-controls="content-${index}" aria-selected="${isActive ? 'true' : 'false'}">
					${category.name}
				</button>
			</li>`;
		tabList.innerHTML += tabHTML;

		// Create content for each tab pane
		let categoryContentHTML = `
			<div class="tab-pane fade ${isActive ? 'show active' : ''}" id="content-${index}" role="tabpanel" aria-labelledby="tab-${index}">
				<div class="techList">`;

		// Generate skills within each category
		category.skills.forEach(skill => {
			let subSkillsHTML = '';
			if (skill.subSkills && skill.subSkills.length > 0) {
				subSkillsHTML = `
					<ul class="list-group list-group-flush">
						${skill.subSkills.map(subSkill => {
							const ratingLetter = subSkill.rating ? subSkill.rating[0].toUpperCase() : '';
							const ratingCSSClass = subSkill.rating ? `rate${ratingLetter === "?" ? "Unknown" : ratingLetter}` : "";
							const ratingTooltip = ratingLetter ? skillRatings[ratingLetter].tooltip : '';

							const subSkillProjects = getProjectsUsingSkill(projects, subSkill.name);
							const subSkillProjectCount = subSkillProjects.length;
							const subSkillProjectNames = subSkillProjects.map(project => `<a href='#${generateProjectId(project.name)}' class='anchor'>${project.name.replace(/"/g, '&quot;')}</a>`).join('<br>');
							
							const subSkillProjectBadge = subSkillProjectCount > 0 ? `<span class="project-badge" 
								data-bs-toggle="popover"
								data-bs-content="${subSkillProjectNames}" data-bs-html="true">
									Used in ${subSkillProjectCount} project${subSkillProjectCount > 1 ? 's' : ''}
								</span>` : '';

							return `
								<li class="list-group-item">
									<div class="d-flex align-items-center">
										<span class="skillTooltip me-2" data-bs-toggle="tooltip"
											data-bs-container="#skills" data-bs-trigger="hover" data-bs-placement="right"
											data-bs-html="true" data-bs-custom-class="skill-rating-tooltip"
											aria-label="Rating: ${subSkill.rating}"
											title="${ratingTooltip}">
											<span class="badge bg-secondary ${ratingCSSClass}">${subSkill.rating}</span>
										</span>
										<span>${subSkill.name}</span>
										${subSkillProjectBadge}
									</div>
									<div class="sub-skill-description">
										${truncateHtml(subSkill.description, 100)} <!-- Show a shortened description -->
									</div>
									${subSkill.description.length > 100 ?
										`<button class="btn btn-link read-more-btn"
											data-bs-toggle="modal" data-bs-target="#skillDescriptionModal"
											data-full-description="${encodeURIComponent(subSkill.description)}"
											data-skill-name="${subSkill.name}">
											Read More
										</button>` : ''
									}
								</li>
							`;
						}).join('')}
					</ul>`;
			}

			const ratingLetter = skill.rating ? skill.rating[0].toUpperCase() : '';
			const ratingCSSClass = skill.rating ? `rate${ratingLetter === "?" ? "Unknown" : ratingLetter}` : "";
			const ratingTooltip = ratingLetter ? skillRatings[ratingLetter].tooltip : '';

			const skillProjects = getProjectsUsingSkill(projects, skill.name);
			const skillProjectCount = skillProjects.length;
			const skillProjectNames = skillProjects.map(project => `<a href='#${generateProjectId(project.name)}' class='anchor'>${project.name.replace(/"/g, '&quot;')}</a>`).join('<br>');
			
			const skillProjectBadge = skillProjectCount > 0 ? `<span class="project-badge" 
				data-bs-toggle="popover"
				data-bs-content="${skillProjectNames}" data-bs-html="true">
					Used in ${skillProjectCount} project${skillProjectCount > 1 ? 's' : ''}
				</span>` : '';

			// Inside the skill card in categoryContentHTML, add a "Read More" button
			categoryContentHTML += `
			<div class="card-container">
				<div class="card skill-card">
					<div class="card-header d-flex align-items-center">
						<span class="skillTooltip me-2" data-bs-toggle="tooltip"
							data-bs-container="#skills" data-bs-trigger="hover" data-bs-placement="right"
							data-bs-html="true" data-bs-custom-class="skill-rating-tooltip"
							aria-label="Rating: ${skill.rating}"
							title="${ratingTooltip}">
							<span class="badge bg-secondary ${ratingCSSClass}">${skill.rating}</span>
						</span>
						<h5 class="card-title">${skill.name}</h5>
						${skillProjectBadge}
					</div>
					<div class="card-body">
						<div class="skill-description">
							${truncateHtml(skill.description, 100)} <!-- Show a shortened description -->
						</div>
						${skill.description.length > 100 ?
							`<button class="btn btn-link read-more-btn"
								data-bs-toggle="modal" data-bs-target="#skillDescriptionModal"
								data-full-description="${encodeURIComponent(skill.description)}"
								data-skill-name="${skill.name}">
								Read More
							</button>` : ''
						}
						${subSkillsHTML}
					</div>
				</div>
			</div>`;

		});

		categoryContentHTML += `</div></div>`;
		tabContent.innerHTML += categoryContentHTML;
	});

	function setupProjectPopovers() {
		const popovers = Array.from(document.querySelectorAll('.project-badge')).map((popoverTriggerEl) => {
			const popover = new Popover(popoverTriggerEl, {
				trigger: 'manual',
				html: true,
				sanitize: false // allows HTML content inside the popover
			});

			// Show and hide popover on click
			popoverTriggerEl.addEventListener("click", (event) => {
				event.stopPropagation();

				// Hide all other popovers before toggling the current one
				popovers.forEach(p => {
					if (p !== popover) p.hide();
				});

				popover.toggle();
			});

			// Close popover when clicking outside of it
			document.addEventListener("click", (event) => {
				if (!popoverTriggerEl.contains(event.target as Node) && document.querySelector('.popover')?.contains(event.target as Node) === false) {
					popover.hide();
				}
			});

			// Close popover when clicking a link inside it
			popoverTriggerEl.addEventListener("shown.bs.popover", () => {
				const popoverContent = document.querySelector('.popover');
				popoverContent?.addEventListener("click", (event) => {
					if ((event.target as HTMLElement).tagName === "A") {
						popover.hide();
					}
				});
			});

			return popover;
		});
	}

	function setupReadMoreModal() {
		const modalElement = document.getElementById('skillDescriptionModal');
		const modal = new Modal(modalElement!);

		document.querySelectorAll('.read-more-btn').forEach(button => {
			button.addEventListener('click', () => {
				// Get and decode the full description
				const fullDescriptionEncoded = button.getAttribute('data-full-description') || '';
				const fullDescription = decodeURIComponent(fullDescriptionEncoded);

				// Get the skill name
				const skillName = button.getAttribute('data-skill-name') || '';

				// Set the decoded HTML content and skill name in the modal
				document.getElementById('fullSkillDescription')!.innerHTML = fullDescription;
				document.getElementById('skillDescriptionModalLabel')!.textContent = skillName;

				modal.show();
			});
		});

		// Add click event listener for links inside the modal to close the modal
		modalElement!.querySelector('.modal-body')!.addEventListener('click', (event) => {
			const targetElement = event.target as HTMLElement;
			if (targetElement.tagName === 'A' && targetElement.getAttribute('target') !== '_blank') {
				modal.hide();
			}
		});
	}

	// Initialize tooltips and popovers after DOM update
	requestAnimationFrame(() => {
		createTooltips('.skillTooltip');
		setupProjectPopovers();
		setupReadMoreModal();
		initReadMore();

		$(".skill-card .card-body").mCustomScrollbar({
			axis: "y",
			theme: "minimal-dark",
			autoDraggerLength: true,
			keyboard: { 
				scrollAmount: 5
			}
		});
	});
}

/**
 * Helper function to retrieve projects that use a specific skill.
 */
function getProjectsUsingSkill(projects: IProject[], skillName: string): { name: string, url: string | string[] }[] {
	const skillNameLowerCase = skillName.toLowerCase();

	return projects
		.filter(project => project.technologies && project.technologies.some(tech => {
			const techName = (typeof tech === 'string' ? tech : tech.name).toLowerCase();
	
			if (skillNameLowerCase === 'c') {
				// Only match exactly 'c' to avoid matching 'c++' or 'c#'
				return techName === 'c';
			} else if (skillNameLowerCase.startsWith('qt') && skillNameLowerCase !== 'qt creator') {
				if (techName.startsWith( 'qt creator')) {
					return false;
				} else if (techName.startsWith('qt')) {
					return true;
				} else {
					return false;
				}
			} else if (skillNameLowerCase === 'java') {
				return techName === 'java' || techName.startsWith('java ');
			}
			else {
				return techName.startsWith(skillNameLowerCase);
			}
		}))
		.map(project => ({ name: project.name, url: project.url }));
}


function generateLegend() {
	const container = document.getElementById('skillLegend');
	if (!container) return;

	let legendHTML = 'Personal ratings: <div class="legend-container">';

	Object.keys(skillRatings).forEach(key => {
		if (key === '?') return; // Skip the unknown rating
		const { title, description, tooltip } = skillRatings[key];
		const ratingCSSClass = `rate${key}`;
		legendHTML += `
			<div class="legend-item">
				<span class="skillTooltip" data-bs-toggle="tooltip"
					data-bs-container="#skills" data-bs-trigger="hover" data-bs-placement="right"
					data-bs-html="true" data-bs-custom-class="skill-rating-tooltip"
					aria-label="Rating: ${key}"
					title="${tooltip}">
						<span class="badge ${ratingCSSClass}">${key}</span>
				</span>
				<span class="legend-title">${title}</span>
			</div>`;
	});

	legendHTML += '</div>';
	
	// Insert generated HTML into the container
	container.innerHTML = legendHTML;
}

function setUpNavScroll() {

	const categoryTabsContainer = $('#categoryTabsContainer');
	const categoryTabs = $('#categoryTabs');
	const navLinks = categoryTabs.find('.nav-link');
	let debounceTimer: ReturnType<typeof setTimeout> | null = null;

	navLinks.on('click', function (e) {
		scrollToCenter($(this));
	});

	navLinks.on('keydown', function (e) {
		e.preventDefault(); // Prevent default behavior like page scrolling
		e.stopPropagation(); // Stop Bootstrap's internal handlers

		if (e.key === 'Home' || e.key === 'End') {
			disableMCustomScrollbar();
		}

		if (debounceTimer) {
			clearTimeout(debounceTimer);
		}

		debounceTimer = setTimeout(() => {
			let newTab: JQuery<HTMLElement> | undefined;

			if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
				// Move to the next tab
				newTab = $(this).parent().next().find('.nav-link');
			} else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
				// Move to the previous tab
				newTab = $(this).parent().prev().find('.nav-link');
			} else if (e.key === 'Home') {
				// Move to the first tab
				newTab = $(this).closest('.nav-tabs').find('.nav-link:first');
			} else if (e.key === 'End') {
				// Move to the last tab
				newTab = $(this).closest('.nav-tabs').find('.nav-link:last');
			}

			if (newTab && newTab.length) {
				newTab.tab('show');
				newTab.trigger('click'); // Simulate a click to trigger scrolling
			}

			enableMCustomScrollbar();
		}, 150); // Delay between keydown events
	});

	function scrollToCenter(tab: JQuery<HTMLElement>) {
		const tabOffset = tab.offset().left - categoryTabs.offset().left;
		const tabWidth = tab.outerWidth();
		const containerWidth = categoryTabsContainer.width();

		// Calculate the position to scroll to center the tab
		let scrollPosition = tabOffset - (containerWidth / 2) + (tabWidth / 2);

		// Ensure the scroll position stays within the bounds of the container
		const maxScroll = categoryTabs[0].scrollWidth - categoryTabsContainer.width();
		scrollPosition = Math.min(Math.max(scrollPosition, 0), maxScroll);

		// Use native scrollLeft to scroll the container
		categoryTabsContainer.animate({
			scrollLeft: scrollPosition
		}, 300); // You can adjust the duration of the scroll
	}

	// Center the default active tab
	const activeTab = categoryTabs.find('.nav-item.active');
	if (activeTab.length) {
		scrollToCenter(activeTab);
	}
}

/**
 * Temporarily disable mCustomScrollbar scroll functionality.
 * @param container JQuery<HTMLElement> - The container with mCustomScrollbar.
 */
function disableMCustomScrollbar() {
	$("#skills .panel-body").mCustomScrollbar('disable'); // Disables user interaction
}

/**
 * Re-enable mCustomScrollbar scroll functionality.
 * @param container JQuery<HTMLElement> - The container with mCustomScrollbar.
 */
function enableMCustomScrollbar() {
	setTimeout(() => {
		$("#skills .panel-body").mCustomScrollbar('update'); // Re-enables user interaction
	}, 300); // Adjust based on scroll animation duration
}

function enableHorizontalScrollWithMouseWheel() {
	//const categoryTabsContainer = $('#categoryTabsContainer');
	const categoryTabs = $('#categoryTabs');
	const panelBody = $('#skills .panel-body');

	// Add a wheel event listener to the categoryTabsContainer
	categoryTabs.on('wheel', function (e) {

		// Cast the event to a WheelEvent to access deltaY and deltaX
		const wheelEvent = e.originalEvent as WheelEvent;

		panelBody.mCustomScrollbar("disable");

		// Define the scroll amount and speeds for vertical and horizontal scroll
		const verticalScrollSpeed = 1; // Adjust this for vertical scrolling speed
		const horizontalScrollSpeed = 1.5; // Adjust this for horizontal scrolling speed

		// Determine the scroll amount for vertical and horizontal scrolling
		const scrollAmountVertical = wheelEvent.deltaY * verticalScrollSpeed;
		const scrollAmountHorizontal = wheelEvent.deltaX * horizontalScrollSpeed;

		// If the user is scrolling vertically, adjust vertical scroll
		if (wheelEvent.deltaY !== 0) {
			categoryTabs.scrollLeft(categoryTabs.scrollLeft() + scrollAmountVertical);
		}

		// If the user is scrolling horizontally, adjust horizontal scroll
		if (wheelEvent.deltaX !== 0) {
			categoryTabs.scrollLeft(categoryTabs.scrollLeft() - scrollAmountHorizontal); // Reverse the direction to scroll correctly
		}

		panelBody.mCustomScrollbar("update");
	});
}

export function skillsSetup(projects: IProject[]) {
	loadSkillsJson()
	.then((skills) => {
		fillLanguageSkills(projects, {numLang: 6, toHide: ["html", "css"]});
		generateLegend();
		fillSkillSection(skills, projects);
		setUpNavScroll();
		enableHorizontalScrollWithMouseWheel();
		refreshAOS(); // Refresh AOS to recognize new elements
	})
	.catch((e) => {
		displayError(e.message);
	});
}
